// Limpeza de caches do cliente para sincronizar com novas versões do app.
//
// IMPORTANTE: este módulo NÃO toca em nada que represente preferência ou
// sessão do usuário (token, tema, volume, filtros do kanban, histórico da
// MessagesAPI, etc). Limpa somente o que pode estar "preso" em uma versão
// antiga: caches do Service Worker, registrations e chaves efêmeras do
// próprio sistema de versionamento.

const LEGACY_VERSION_KEYS = [
	"appVersion",          // legado do api.js antigo, antes do BUILD_HASH inlined
];

const SESSION_FLAGS = [
	"__sw_cleanup_done__", // flag do bootstrap em index.html
];

const safe = async (fn) => {
	try { return await fn(); } catch { return null; }
};

const clearCacheStorage = async () => {
	if (typeof window === "undefined" || !("caches" in window)) return 0;
	const keys = await safe(() => caches.keys()) || [];
	await Promise.all(keys.map(k => safe(() => caches.delete(k))));
	return keys.length;
};

const unregisterAllServiceWorkers = async () => {
	if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return 0;
	const regs = await safe(() => navigator.serviceWorker.getRegistrations()) || [];
	await Promise.all(regs.map(r => safe(() => r.unregister())));
	return regs.length;
};

const clearLegacyVersionStorage = () => {
	if (typeof localStorage === "undefined") return 0;
	let removed = 0;
	for (const k of LEGACY_VERSION_KEYS) {
		try {
			if (localStorage.getItem(k) !== null) {
				localStorage.removeItem(k);
				removed++;
			}
		} catch { /* ignora */ }
	}
	return removed;
};

const clearVersionSessionFlags = () => {
	if (typeof sessionStorage === "undefined") return 0;
	let removed = 0;
	for (const k of SESSION_FLAGS) {
		try {
			if (sessionStorage.getItem(k) !== null) {
				sessionStorage.removeItem(k);
				removed++;
			}
		} catch { /* ignora */ }
	}
	return removed;
};

/**
 * Limpa SOMENTE caches relacionados a versionamento. Preserva token, tema,
 * preferências de áudio, filtros do kanban, histórico de MessagesAPI etc.
 * Idempotente — pode ser chamado várias vezes sem efeitos colaterais.
 */
export const clearVersionCaches = async () => {
	const result = {
		swCachesCleared: 0,
		swRegistrationsRemoved: 0,
		legacyKeysRemoved: 0,
		sessionFlagsRemoved: 0,
	};
	result.swCachesCleared = await clearCacheStorage();
	result.swRegistrationsRemoved = await unregisterAllServiceWorkers();
	result.legacyKeysRemoved = clearLegacyVersionStorage();
	result.sessionFlagsRemoved = clearVersionSessionFlags();
	return result;
};

const fetchLatestBuildHash = async () => {
	try {
		const r = await fetch(`/version.json?_=${Date.now()}`, {
			cache: "no-store",
			credentials: "omit",
		});
		if (!r.ok) return null;
		const j = await r.json();
		if (j && typeof j.buildHash === "string" && j.buildHash) return j.buildHash;
	} catch { /* ignora */ }
	return null;
};

const warmHtmlBypassingHttpCache = async (bust) => {
	// `cache: 'reload'` força o browser a revalidar diretamente na origem,
	// ignorando ETag/Last-Modified que poderia causar 304 com index.html velho.
	try {
		await fetch(`/?_swrefresh=${encodeURIComponent(bust)}`, {
			cache: "reload",
			credentials: "same-origin",
			headers: { "Cache-Control": "no-cache, no-store, must-revalidate" },
		});
	} catch { /* ignora */ }
};

/**
 * Limpa todos os caches de versão e força um reload "à prova de cache":
 *   1. Limpa Cache Storage
 *   2. Desregistra todos os SWs
 *   3. Remove chaves legadas/efêmeras
 *   4. Busca buildHash atual no servidor (cache-buster determinístico)
 *   5. Pré-baixa index.html bypassando HTTP cache do browser
 *   6. Navega via location.assign para URL totalmente nova com ?_v=hash
 *      (descarta query antiga; URL diferente = HTTP cache não pode reusar)
 */
export const forceReloadFresh = async () => {
	await clearVersionCaches();

	const buildHash = await fetchLatestBuildHash();
	const bust = buildHash || Date.now().toString(36);

	await warmHtmlBypassingHttpCache(bust);

	if (typeof window === "undefined") return;
	const url = new URL(window.location.origin + window.location.pathname);
	url.searchParams.set("_v", bust);
	window.location.assign(url.toString());
};

export default { clearVersionCaches, forceReloadFresh };
